'use client'

import {
  CanAccess,
  useLink,
  useMenu,
  useRefineOptions,
  type TreeMenuItem,
} from '@refinedev/core'
import { ChevronRight, ListIcon } from 'lucide-react'
import React from 'react'
import { Button } from '~/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdownMenu'
import {
  Sidebar as ShadcnSidebar,
  SidebarContent as ShadcnSidebarContent,
  SidebarHeader as ShadcnSidebarHeader,
  SidebarRail as ShadcnSidebarRail,
  SidebarTrigger as ShadcnSidebarTrigger,
  useSidebar as useShadcnSidebar,
} from '~/components/ui/sidebar'
import { isMenuKeySelected } from '~/lib/menuKey'
import { cn } from '~/lib/utils'

export function Sidebar() {
  const { open } = useShadcnSidebar()
  const { menuItems, selectedKey } = useMenu()

  return (
    <ShadcnSidebar
      collapsible="icon"
      className={cn('border-none')}
    >
      <ShadcnSidebarRail />
      <SidebarHeader />
      <ShadcnSidebarContent
        className={cn(
          'transition-discrete',
          'duration-200',
          'flex',
          'flex-col',
          'gap-2',
          'pt-2',
          'pb-2',
          'border-r',
          'border-border',
          {
            'px-3': open,
            'px-1': !open,
          },
        )}
      >
        {menuItems.map((item: TreeMenuItem) => (
          <SidebarItem
            key={item.key || item.name}
            item={item}
            selectedKey={selectedKey}
          />
        ))}
      </ShadcnSidebarContent>
    </ShadcnSidebar>
  )
}

type MenuItemProps = {
  item: TreeMenuItem
  selectedKey?: string
}

/**
 * An entry of the menu, or of a section's children: hidden when access
 * control denies its list (Platform and its pages for tenant users, most of
 * them for a technician).
 */
function SidebarItem({ item, selectedKey }: MenuItemProps) {
  return (
    <CanAccess
      resource={item.name}
      action="list"
    >
      <SidebarItemContent
        item={item}
        selectedKey={selectedKey}
      />
    </CanAccess>
  )
}

function SidebarItemContent({ item, selectedKey }: MenuItemProps) {
  const { open } = useShadcnSidebar()

  if (Boolean(item.meta?.group)) {
    return (
      <SidebarItemGroup
        item={item}
        selectedKey={selectedKey}
      />
    )
  }

  if (item.children.length > 0) {
    if (open) {
      return (
        <SidebarItemCollapsible
          item={item}
          selectedKey={selectedKey}
        />
      )
    }
    return (
      <SidebarItemDropdown
        item={item}
        selectedKey={selectedKey}
      />
    )
  }

  return (
    <SidebarItemLink
      item={item}
      selectedKey={selectedKey}
    />
  )
}

function SidebarItemGroup({ item, selectedKey }: MenuItemProps) {
  const { children } = item
  const { open } = useShadcnSidebar()

  return (
    <div className={cn('border-t', 'border-sidebar-border', 'pt-4')}>
      <span
        className={cn(
          'ml-3',
          'block',
          'text-xs',
          'font-semibold',
          'uppercase',
          'text-muted-foreground',
          'transition-all',
          'duration-200',
          {
            'h-8': open,
            'h-0': !open,
            'opacity-0': !open,
            'opacity-100': open,
            'pointer-events-none': !open,
            'pointer-events-auto': open,
          },
        )}
      >
        {getDisplayName(item)}
      </span>
      {children.length > 0 && (
        <div className={cn('flex', 'flex-col')}>
          {children.map((child: TreeMenuItem) => (
            <SidebarItem
              key={child.key || child.name}
              item={child}
              selectedKey={selectedKey}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function SidebarItemCollapsible({ item, selectedKey }: MenuItemProps) {
  const { name, children } = item

  const chevronIcon = (
    <ChevronRight
      className={cn(
        'h-4',
        'w-4',
        'shrink-0',
        'text-muted-foreground',
        'transition-transform',
        'duration-200',
        'group-data-[state=open]:rotate-90',
      )}
    />
  )

  return (
    <Collapsible
      key={`collapsible-${name}`}
      className={cn('w-full', 'group')}
      defaultOpen={children.some((child) =>
        isMenuKeySelected(child.key, selectedKey ?? ''),
      )}
    >
      <CollapsibleTrigger asChild>
        <SidebarButton
          item={item}
          rightIcon={chevronIcon}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className={cn('ml-6', 'flex', 'flex-col', 'gap-2')}>
        {children?.map((child: TreeMenuItem) => (
          <SidebarItem
            key={child.key || child.name}
            item={child}
            selectedKey={selectedKey}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

function SidebarItemDropdown({ item, selectedKey }: MenuItemProps) {
  const { children } = item
  const Link = useLink()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarButton item={item} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
      >
        {children?.map((child: TreeMenuItem) => {
          const { key: childKey } = child
          const isSelected = isMenuKeySelected(childKey, selectedKey ?? '')

          return (
            <CanAccess
              key={childKey ?? child.name}
              resource={child.name}
              action="list"
            >
              <DropdownMenuItem asChild>
                <Link
                  to={child.route ?? ''}
                  className={cn('flex w-full items-center gap-2', {
                    'bg-accent text-accent-foreground': isSelected,
                  })}
                >
                  <ItemIcon
                    icon={child.meta?.icon ?? child.icon}
                    isSelected={isSelected}
                  />
                  <span>{getDisplayName(child)}</span>
                </Link>
              </DropdownMenuItem>
            </CanAccess>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SidebarItemLink({ item, selectedKey }: MenuItemProps) {
  const isSelected = isMenuKeySelected(item.key, selectedKey ?? '')

  return (
    <SidebarButton
      item={item}
      isSelected={isSelected}
      asLink={true}
    />
  )
}

function SidebarHeader() {
  const { title } = useRefineOptions()
  const { open, isMobile } = useShadcnSidebar()

  return (
    <ShadcnSidebarHeader
      className={cn(
        'p-0',
        'h-16',
        'border-b',
        'border-border',
        'flex-row',
        'items-center',
        'justify-between',
        'overflow-hidden',
      )}
    >
      <div
        className={cn(
          'whitespace-nowrap',
          'flex',
          'flex-row',
          'h-full',
          'items-center',
          'justify-start',
          'gap-2',
          'transition-discrete',
          'duration-200',
          {
            'pl-3': !open,
            'pl-5': open,
          },
        )}
      >
        <div>{title.icon}</div>
        <h2
          className={cn(
            'text-sm',
            'font-bold',
            'transition-opacity',
            'duration-200',
            {
              'opacity-0': !open,
              'opacity-100': open,
            },
          )}
        >
          {title.text}
        </h2>
      </div>

      <ShadcnSidebarTrigger
        className={cn('text-muted-foreground', 'mr-1.5', {
          'opacity-0': !open,
          'opacity-100': open || isMobile,
          'pointer-events-auto': open || isMobile,
          'pointer-events-none': !open && !isMobile,
        })}
      />
    </ShadcnSidebarHeader>
  )
}

function getDisplayName(item: TreeMenuItem) {
  return item.meta?.label ?? item.label ?? item.name
}

type IconProps = {
  icon: React.ReactNode
  isSelected?: boolean
}

function ItemIcon({ icon, isSelected }: IconProps) {
  return (
    <div
      className={cn('w-4', {
        'text-muted-foreground': !isSelected,
        'text-sidebar-primary-foreground': isSelected,
      })}
    >
      {icon ?? <ListIcon />}
    </div>
  )
}

type SidebarButtonProps = React.ComponentProps<typeof Button> & {
  item: TreeMenuItem
  isSelected?: boolean
  rightIcon?: React.ReactNode
  asLink?: boolean
  onClick?: () => void
}

function SidebarButton({
  item,
  isSelected = false,
  rightIcon,
  asLink = false,
  className,
  onClick,
  ...props
}: SidebarButtonProps) {
  const Link = useLink()

  const buttonContent = (
    <>
      <ItemIcon
        icon={item.meta?.icon ?? item.icon}
        isSelected={isSelected}
      />
      <span
        className={cn('tracking-[-0.00875rem]', {
          'flex-1': Boolean(rightIcon),
          'text-left': Boolean(rightIcon),
          'line-clamp-1': !Boolean(rightIcon),
          truncate: !Boolean(rightIcon),
          'font-normal': !isSelected,
          'font-semibold': isSelected,
          'text-sidebar-primary-foreground': isSelected,
          'text-foreground': !isSelected,
        })}
      >
        {getDisplayName(item)}
      </span>
      {rightIcon}
    </>
  )

  return (
    <Button
      asChild={asLink && Boolean(item.route)}
      variant="ghost"
      size="lg"
      className={cn(
        'flex w-full items-center justify-start gap-2 py-2 !px-3 text-sm',
        {
          'bg-sidebar-primary': isSelected,
          'hover:!bg-sidebar-primary/90': isSelected,
          'text-sidebar-primary-foreground': isSelected,
          'hover:text-sidebar-primary-foreground': isSelected,
        },
        className,
      )}
      onClick={onClick}
      {...props}
    >
      {asLink && item.route !== undefined ? (
        <Link
          to={item.route}
          className={cn('flex w-full items-center gap-2')}
        >
          {buttonContent}
        </Link>
      ) : (
        buttonContent
      )}
    </Button>
  )
}

Sidebar.displayName = 'Sidebar'
